//angular imports
import {
  ChangeDetectionStrategy,
  Component,
  WritableSignal,
  input, signal,
} from '@angular/core';

import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';

//third party
import { firstValueFrom, lastValueFrom, tap } from 'rxjs';

//local imports
import { ApplicationConfiguration } from '@core/configuration';
import { CurrentUserModel } from '@core/data/models';
import { ApiConstants } from '@shared/data/http/api-constants';

type AvatarUploadTokenResponse = {
  uploadUrl: string;
  blobPath: string;
};

@Component({
  selector: 'avatar-upload',
  standalone: false,
  templateUrl: './avatar-upload.component.html',
  styleUrl: './avatar-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarUploadComponent {
  public currentUser = input<CurrentUserModel | null>(null);

  public uploadProgress: WritableSignal<number | null> = signal<number | null>(null);
  public isUploading: WritableSignal<boolean> = signal<boolean>(false);

  constructor(
    private http: HttpClient,
    private applicationConfig: ApplicationConfiguration,
  ) {}

  public selectAvatar(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  public async avatarSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    const currentUser = this.currentUser();
    if (!file || !currentUser?.id) return;

    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      console.warn('AvatarUploadComponent: unsupported file type', file.type);
      return;
    }

    await this.uploadAvatar(currentUser.id, file);
  }

  //TODO:DIW:Should be in a http service
  private async uploadAvatar(userId: string, file: File): Promise<void> {
    this.isUploading.set(true);
    this.uploadProgress.set(0);

    try {
      const tokenResponse = await firstValueFrom(this.http.post<AvatarUploadTokenResponse>(
        `${this.applicationConfig.applicationUri}/core/aaa/users/${userId}/avatar/upload-token`,
        { fileName: file.name, contentType: file.type, size: file.size },
        { headers: ApiConstants.DefaultHeaders },
      ));

      await lastValueFrom(this.http.put(tokenResponse.uploadUrl, file, {
        headers: new HttpHeaders({
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type,
        }),
        observe: 'events',
        reportProgress: true,
      }).pipe(tap(event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress.set(Math.round((event.loaded / event.total) * 100));
        }
      })));

      await firstValueFrom(this.http.post(
        `${this.applicationConfig.applicationUri}/core/aaa/users/${userId}/avatar`,
        { blobPath: tokenResponse.blobPath },
        { headers: ApiConstants.DefaultHeaders },
      ));

      this.uploadProgress.set(100);
    } catch (error) {
      console.error('AvatarUploadComponent: upload failed', error);
    } finally {
      this.isUploading.set(false);
      setTimeout(() => this.uploadProgress.set(null), 750);
    }
  }
}